import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useUpdateLeadMutation } from '../../api/leadsApi';
import type { Lead } from '../../api/leadsApi';

interface LeadDetailProps {
  isOpen: boolean;
  lead: Lead | null;
  onClose: () => void;
  onEdit?: (lead: Lead) => void;
}

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 1100;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const EditButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #1a202c;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  color: #4a5568;
  padding: 4px 8px;
  transition: color 0.15s;

  &:hover {
    color: #1a202c;
  }
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
`;

const InfoList = styled.dl`
  display: grid;
  grid-template-columns: minmax(120px, 200px) 1fr;
  row-gap: 8px;
  column-gap: 16px;
  font-size: 14px;
`;

const Term = styled.dt`
  color: #4a5568;
`;

const Desc = styled.dd`
  margin: 0;
  color: #2d3748;
  word-break: break-word;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  
  ${({ status }) => {
    switch (status) {
      case 'new':
        return 'background: #bee3f8; color: #2a4365;';
      case 'contacted':
        return 'background: #fef5e7; color: #744210;';
      case 'qualified':
        return 'background: #c6f6d5; color: #22543d;';
      case 'closed':
        return 'background: #e2e8f0; color: #4a5568;';
      default:
        return 'background: #e2e8f0; color: #4a5568;';
    }
  }}
`;

const MessageBox = styled.div`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 12px;
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #2d3748;
  white-space: pre-wrap;
`;

const QuickStatusUpdate = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 12px;
`;

const StatusSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 12px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

const UpdateButton = styled.button`
  background: #4299e1;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3182ce;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

export const LeadDetail: React.FC<LeadDetailProps> = ({ isOpen, lead, onClose, onEdit }) => {
  const [updateLead, { isLoading: isUpdating }] = useUpdateLeadMutation();
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [notes, setNotes] = useState('');

  React.useEffect(() => {
    if (lead) {
      setSelectedStatus(lead.status);
      setNotes(lead.notes || '');
    }
  }, [lead]);

  if (!lead) return null;

  const handleEdit = () => {
    if (onEdit) {
      onEdit(lead);
    }
  };

  const handleQuickStatusUpdate = async () => {
    if (selectedStatus === lead.status && notes === (lead.notes || '')) {
      return;
    }

    try {
      await updateLead({
        id: lead.id,
        updates: {
          status: selectedStatus as 'new' | 'contacted' | 'qualified' | 'closed',
          notes: notes || undefined,
        },
      }).unwrap();
    } catch (error) {
      console.error('Failed to update lead:', error);
    }
  };

  const hasChanges = selectedStatus !== lead.status || notes !== (lead.notes || '');

  return (
    <Overlay isOpen={isOpen} onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{lead.name}</Title>
          <ActionButtons>
            {onEdit && (
              <EditButton onClick={handleEdit}>
                Edit Lead
              </EditButton>
            )}
            <CloseButton aria-label="Close lead details" onClick={onClose}>
              &times;
            </CloseButton>
          </ActionButtons>
        </Header>

        <Body>
          <Section>
            <SectionTitle>Contact Information</SectionTitle>
            <InfoList>
              <Term>Lead ID</Term>
              <Desc>{lead.id}</Desc>

              <Term>Name</Term>
              <Desc>{lead.name}</Desc>

              <Term>Email</Term>
              <Desc>{lead.email}</Desc>

              {lead.phone && (
                <>
                  <Term>Phone</Term>
                  <Desc>{lead.phone}</Desc>
                </>
              )}

              <Term>Source</Term>
              <Desc>
                <code style={{ background: '#f7fafc', padding: '2px 6px', borderRadius: 4 }}>
                  {lead.source}
                </code>
              </Desc>

              <Term>Created</Term>
              <Desc>{new Date(lead.createdAt).toLocaleString()}</Desc>

              {lead.updatedAt && (
                <>
                  <Term>Last Updated</Term>
                  <Desc>{new Date(lead.updatedAt).toLocaleString()}</Desc>
                </>
              )}
            </InfoList>
          </Section>

          <Section>
            <SectionTitle>Status & Notes</SectionTitle>
            <InfoList>
              <Term>Current Status</Term>
              <Desc>
                <StatusBadge status={lead.status}>{lead.status}</StatusBadge>
              </Desc>
            </InfoList>

            <QuickStatusUpdate>
              <StatusSelect
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="closed">Closed</option>
              </StatusSelect>
              <UpdateButton
                onClick={handleQuickStatusUpdate}
                disabled={!hasChanges || isUpdating}
              >
                {isUpdating ? 'Updating...' : 'Update Status'}
              </UpdateButton>
            </QuickStatusUpdate>

            <div style={{ marginTop: '16px' }}>
              <Term style={{ marginBottom: '8px', display: 'block' }}>Notes</Term>
              <NotesTextarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this lead..."
              />
            </div>
          </Section>

          {lead.message && (
            <Section>
              <SectionTitle>Original Message</SectionTitle>
              <MessageBox>{lead.message}</MessageBox>
            </Section>
          )}

          <Section>
            <SectionTitle>Customer Information</SectionTitle>
            <InfoList>
              <Term>Customer</Term>
              <Desc>{lead.customer.name}</Desc>

              <Term>Email</Term>
              <Desc>{lead.customer.email}</Desc>

              {lead.customer.company && (
                <>
                  <Term>Company</Term>
                  <Desc>{lead.customer.company}</Desc>
                </>
              )}
            </InfoList>
          </Section>

          {lead.landingPage && (
            <Section>
              <SectionTitle>Landing Page</SectionTitle>
              <InfoList>
                <Term>Page Title</Term>
                <Desc>{lead.landingPage.title}</Desc>

                <Term>Slug</Term>
                <Desc>
                  <code style={{ background: '#f7fafc', padding: '2px 6px', borderRadius: 4 }}>
                    /{lead.landingPage.slug}
                  </code>
                </Desc>

                {lead.landingPage.template && (
                  <>
                    <Term>Template</Term>
                    <Desc>{lead.landingPage.template}</Desc>
                  </>
                )}
              </InfoList>
            </Section>
          )}
        </Body>
      </Modal>
    </Overlay>
  );
};

export default LeadDetail;